// Wishes.tsx — kanban-style wishlist home (W33.1 desktop + W33.2 mobile port
// per `docs/spec/wishlist/DESIGN.md §3.5 + §5`).
//
// Desktop (≥ 1024px): 4-column kanban (KanbanBoard). Tap a card → navigate
//   to /wishes/:id. Drag a card → PATCH /api/v1/wishes/{id} (optimistic
//   cache + rollback on error).
// Mobile (< 768px): 4 collapsible sections (per DESIGN §5.3) so the user
//   can fold each status group; tap a card → BottomSheet at the `large`
//   detent (DESIGN §18.7: every sheet is `large`, there is no medium) with
//   primary (view detail) + secondary (mark / abandon / delete)
//   actions. Per the cross-module UX principle (DESIGN §3.5), mobile
//   interaction is sheet-driven — no direct navigate on tap.
//
// Per W33.1 acceptance + W33.2 mobile extension:
//   - 4 statuses (pending / in_progress / completed / abandoned)
//   - Optimistic update on drop (desktop only)
//   - Drag to abandoned → window.confirm warning (calendar mirror)
//   - Mobile tap card → BottomSheet (large detent, not navigate)
//   - BottomSheet primary action → navigate /wishes/:id
//   - BottomSheet secondary actions → execute without leaving sheet
//   - Soft WIP badge on in_progress > 5
//   - touch target ≥ 44pt per Apple HIG (DESIGN §15)

import { Fragment, useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useAuth } from "@/lib/auth/AuthProvider";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { EmptyState } from "@/components/primitives/EmptyState";
import { useKanbanDnd } from "@/hooks/useKanbanDnd";
import { KanbanBoard } from "@/components/wish/KanbanBoard";
import { MobileActionSheet } from "@/components/wish/MobileActionSheet";
import { WishlistAside } from "@/components/wish/AsideStatsPanel";
import {
  listWishes,
  patchWish,
  type WishListResponse,
  type WishStatus,
  type WishSummary,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { toHktToday } from "@/lib/utils/hkt-time";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { BarChart3, Plus, Trash2 } from "lucide-react";

import { MobileCollapsibleSections } from "./wishes/components/MobileCollapsibleSections";
import { WishPeakCard } from "./wishes/components/WishPeakCard";
import { useWishRestore } from "./wishes/useWishRestore";

const WISHES_QUERY_KEY = ["wishes", "list", { visibility: "all" }] as const;

export function WishesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const today = useMemo(() => toHktToday(), []);
  const { user } = useAuth();

  // Sheet state — only meaningful on mobile, but kept unconditional so
  // the Radix sheet stays mounted across route navigations.
  const [sheetWish, setSheetWish] = useState<WishSummary | null>(null);

  // W49.0: the delete → undo → restore concern, extracted to keep this route
  // under the 15 KB ceiling it crossed in P2-4. See `useWishRestore`.
  const restore = useWishRestore();

  const {
    data: wishList,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: WISHES_QUERY_KEY,
    queryFn: () => listWishes({ visibility: "all", limit: 200 }),
    staleTime: 30_000,
  });

  // Memoised so the bucket `useMemo` below (dep: `[wishes]`) keeps a stable
  // identity across renders — `?? []` alone allocates a fresh array every
  // render and defeats the memo (react-hooks/exhaustive-deps).
  const wishes: WishSummary[] = useMemo(() => wishList?.items ?? [], [wishList]);

  const moveMutation = useMutation({
    mutationFn: async (input: { id: string; status: WishStatus }) => {
      return patchWish(input.id, { status: input.status });
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: WISHES_QUERY_KEY });
      const prev = queryClient.getQueryData<WishListResponse>(WISHES_QUERY_KEY);
      if (prev) {
        queryClient.setQueryData<WishListResponse>(WISHES_QUERY_KEY, {
          ...prev,
          items: prev.items.map((w) => (w.id === id ? { ...w, status } : w)),
        });
      }
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) queryClient.setQueryData<WishListResponse>(WISHES_QUERY_KEY, ctx.prev);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WISHES_QUERY_KEY });
    },
  });

  const onMove = useCallback(
    (id: string, status: WishStatus) => {
      moveMutation.mutate({ id, status });
    },
    [moveMutation],
  );

  const dnd = useKanbanDnd({ wishes, onMove, enabled: !isLoading && !isMobile });

  // Desktop: tap card → navigate detail (per DESIGN §3.5 "Desktop 不受影响")
  // Mobile: tap card → open BottomSheet (per DESIGN §3.5 cross-module UX)
  const onCardClick = useCallback(
    (w: WishSummary) => {
      if (isMobile) {
        setSheetWish(w);
      } else {
        navigate(`/wishes/${w.id}`);
      }
    },
    [isMobile, navigate],
  );

  const onNewWish = useCallback(() => {
    navigate("/wishes/new");
  }, [navigate]);

  // Group wishes by status for the mobile-collapsible fallback view
  const grouped = useMemo<Record<WishStatus, WishSummary[]>>(() => {
    const out: Record<WishStatus, WishSummary[]> = {
      pending: [],
      in_progress: [],
      completed: [],
      abandoned: [],
    };
    for (const w of wishes) {
      // A hole in the array is not a contract the type can forbid: `items: [null]`
      // is a 200 response and reaches here unchanged, because the API layer
      // casts with `data as T` and validates nothing. Reading `w.status` on it
      // threw `Cannot read properties of null`, which took the whole board down
      // behind the error boundary — the user's entire wish list replaced by an
      // error card because of one malformed row.
      //
      // A row that is not an object cannot be rendered as a wish, so it is
      // dropped rather than crashing the screen that is trying to show the
      // other 21.
      if (!w || typeof w !== "object" || typeof w.status !== "string") continue;
      const bucket = out[w.status as WishStatus] ?? out.pending;
      bucket.push(w);
    }
    return out;
  }, [wishes]);

  return (
    <PageErrorBoundary label="心願">
      {/*
        W49.0 §6. The hero is a **sibling** of `PageContainer`, not a child.
        `PageHero` carries its own `px-s-4 md:px-s-6` (PageHero.tsx:105), so
        nesting it inside the container's identical padding plus `py-s-5` put
        every wishlist title 16px further right and 20px lower than the other
        30 pages — the hero's own comment used to say it "stays inside the page
        gutter", which is what told five files to do the same wrong thing.
        Thirty pages are the convention; matching them is five files' work
        rather than thirty.
      */}
      <div className="flex flex-col" data-testid="wishes-page">
        <PageHero
          title="心願"
          // Use anniversary baseline: meaningful eyebrow on both viewports
          // (mobile used to read "手機" — a placeholder string left over from
          // W33.2 mobile port). Per /impeccable 2026-09-22 baseline alignment.
          eyebrow="心願板"
          // W52.0 D-1 — the subtitle is **state**, and it is the same string on
          // every viewport.
          //
          // It used to be `${isMobile ? "點心願開動作" : "拖拽到不同狀態切換"}`:
          // an interaction hint whose **length changes with the window**, in the
          // one slot that reserves its height for a count. Two builds of the
          // same screen therefore laid out differently, and what the slot
          // gained was documentation rather than information. 「進行中」 is the
          // state a person actually scans a board for.
          subtitle={`${wishes.length} 個心願 · ${grouped.in_progress.length} 個進行中`}
          trailing={
            isMobile ? (
              // W42 F28 + the §1.2 defect 「垃圾桶按鈕掉到 + 按鈕下方、懸在標題塊
              // 外」: the trash link used to be its own right-aligned row *below*
              // the PageHero, which put it outside the title block and stacked it
              // under the create pill — measured at `y≈368` on
              // `w42-capture-wishes.png`, hanging below the very block it belongs
              // to. It is now a sibling of the `+` inside the hero's trailing
              // slot, so both actions sit on the title's optical line.
              //
              // It is *not* in the topbar: F28 fixes that row at three
              // components (wordmark · bell · avatar) precisely so it stays the
              // one invariant line in 39 routes, and a per-module trash link is
              // route-scoped. The trailing slot is route-scoped, which is
              // exactly what this control is.
              <div className="flex items-center gap-s-2">
                <Link
                  to="/wishes/trash"
                  data-testid="goto-wish-trash"
                  aria-label="心願垃圾桶"
                  className="press shell-btn text-ink-2 hover:bg-black/5 transition-colors duration-quick"
                >
                  <Trash2 className="w-4 h-4" aria-hidden />
                </Link>
                {/* W42 F32/F33: labelled ink pill rather than a 44pt bare `+`
                    circle in you-strong — see `Calendar.tsx` for why the colour
                    was the more serious of the two defects. */}
                <button
                  type="button"
                  onClick={onNewWish}
                  aria-label="新增心願"
                  data-testid="wishes-add-mobile"
                  className="press cta-pill hover:opacity-90 transition-opacity"
                >
                  <Plus size={18} aria-hidden />
                  新增
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/wishes?aside=stats")}
                data-testid="wishes-show-stats"
                className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full border border-hairline text-ink-1 text-body-sm font-semibold"
              >
                <BarChart3 className="h-4 w-4" aria-hidden />
                顯示統計
              </button>
            )
          }
        />
        <PageContainer>
          {isLoading ? (
            <LoadingState shape="card" count={4} />
          ) : isError ? (
            <ErrorState
              title="讀取心願失敗"
              description={error instanceof Error ? error.message : "請稍後再試"}
              onRetry={() => refetch()}
            />
          ) : wishes.length === 0 ? (
            <EmptyState
              title="還沒有心願"
              description="新增你的第一個心願，一起記錄想做的事、想去的地方、想實現的願望。"
              actionLabel="新增心願"
              onAction={onNewWish}
            />
          ) : (
            <Fragment>
              {/* W42 W4/F16 — the screen's peak card, the one it was missing. */}
              <WishPeakCard wishes={wishes} />
              {isMobile ? (
                <MobileCollapsibleSections
                  grouped={grouped}
                  today={today}
                  onCardClick={onCardClick}
                />
              ) : (
                <dnd.DndContextRoot>
                  <KanbanBoard
                    wishes={wishes}
                    today={today}
                    draggable
                    onCardClick={onCardClick}
                    onNewWish={onNewWish}
                  />
                </dnd.DndContextRoot>
              )}
            </Fragment>
          )}
          {/* W34.3.2: ConfirmDialog for abandoned-column drops. The dialog
            lives outside DndContextRoot so its overlay can sit above the
            kanban. Mobile auto-promotes to BottomSheet via ConfirmDialog's
            internal useSheetVariant hook. */}
          <dnd.AbandonedConfirm />
          {/* Mobile sheet — renders even when no wish is selected so
            the Radix root stays mounted between opens. */}
          <MobileActionSheet
            wish={sheetWish}
            onOpenChange={(open) => {
              if (!open) setSheetWish(null);
            }}
            onDeleted={restore.onDeleted}
            viewerId={user?.id}
          />
          {/* W39.1 R1 (ADR-0080): the board owns the undo bar, not the sheet —
            the sheet has already closed by the time a delete succeeds. Fixed
            and 108px up for the two reasons the screenshot pass proved: it must
            not scroll away, and it must clear the three-island pill. */}
          {restore.item ? (
            <div
              data-testid="wish-undo-slot"
              className="pointer-events-none fixed inset-x-0 bottom-[108px] z-40 flex justify-center px-4"
            >
              <div className="pointer-events-auto relative w-full max-w-md">
                <UndoBar
                  open
                  message={`已刪除「${restore.item.name}」`}
                  // W49.0 P2-4 — `undefined` for someone else's wish. The bar
                  // still says what happened; the button does not appear,
                  // because the only thing it could do is fail.
                  onUndo={restore.item.restorable ? restore.onUndo : undefined}
                  className="inset-x-0"
                />
              </div>
            </div>
          ) : null}
          {/* Desktop aside (per DESIGN §5.5) — URL sync via ?aside=stats.
            Mobile path uses BottomSheet 全宽 80vh (W34.0 follow-up). */}
          <WishlistAside />
        </PageContainer>
      </div>
    </PageErrorBoundary>
  );
}

// --- Mobile collapsible sections (DESIGN §5.3) ----------------------------
